import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { get, sortBy } from 'lodash';
import { useI18n } from 'vue-i18n';

import { LuxonDateTimeFormats, NovaDate } from '@nova/core';

import yardTasksService from '@satellite/services/yard-tasks-service';
import { useNotify } from '@satellite/composables/useNotify';
import { useEventHub } from '@satellite/composables/useEventHub';

import type { Ref } from 'vue';
import type { YardTaskTimelineEvent } from '@satellite/components/YardTaskTimeline/types';

export type CommentSubspaceEvent = {
  id: string;
  createDateTime: string;
  createdBy: string;
  lastChangedDateTime: string;
  lastChangedBy: string;
  isActive: boolean;
  tags: null | string[];
  relatedEntity: string;
  relatedId: string;
  htmlContent: string;
  userId: string;
  constructor: {
    name: string;
  };
  debounce: {
    count: number;
    ids: string[];
  };
  action: string;
};

export type YardTaskSubspaceEvent = {
  id: string;
  createDateTime: string;
  createdBy: string;
  lastChangedDateTime: string;
  lastChangedBy: string | null;
  isActive: boolean;
  tags: null | string[];
  orgId: string;
  warehouseId: string;
  yardTaskId: string;
  event: string;
  reason: string | null;
  data: {
    user: {
      id: string;
      lastName: string;
      firstName: string;
    };
    [key: string]: unknown;
  };
  constructor: {
    name: string;
  };
  debounce: {
    count: number;
    ids: string[];
  };
  action: string;
};

export interface UseYardTaskTimelineOptions {
  taskId: Ref<string | null | undefined>;
  warehouseTimezone: Ref<string>;
  onEventsUpdated?: (events: YardTaskTimelineEvent[]) => void;
}

export function useYardTaskTimeline(options: UseYardTaskTimelineOptions) {
  const { locale, t } = useI18n();
  const { notify } = useNotify();
  const eventHub = useEventHub();

  const isLoading = ref(false);
  const timelineEvents = ref<YardTaskTimelineEvent[]>([]);

  const fetchTimeline = async (id: string) => {
    if (!id) {
      timelineEvents.value = [];
      return;
    }

    isLoading.value = true;

    try {
      const rawResponse = await yardTasksService.getYardTaskTimeline(id);
      const events = Array.isArray(rawResponse) ? rawResponse : [];

      timelineEvents.value = events;
    } catch (error) {
      notify(t('global.timeline.failedToFetchYardTaskTimeline'), 'error');

      timelineEvents.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  watch(
    () => options.taskId.value,
    newTaskId => {
      if (!newTaskId) {
        timelineEvents.value = [];
        return;
      }

      fetchTimeline(newTaskId);
    },
    { immediate: true }
  );

  watch(timelineEvents, () => {
    nextTick(() => {
      options.onEventsUpdated?.(timelineEvents.value);
    });
  });

  const groupedEvents = computed(() => {
    const allEvents = Array.isArray(timelineEvents.value) ? timelineEvents.value : [];

    const groupedByDate: Record<string, YardTaskTimelineEvent[]> = {};
    allEvents.forEach(event => {
      const date = new NovaDate(event.data.createDateTime).format(
        LuxonDateTimeFormats.DateWithAbbreviatedMonth,
        {
          tz: options.warehouseTimezone.value,
          locale: locale.value
        }
      );
      groupedByDate[date] = [...(groupedByDate[date] || []), event];
    });

    // Sort events within each group by createDateTime (oldest first)
    Object.keys(groupedByDate).forEach(date => {
      groupedByDate[date] = sortBy(groupedByDate[date], 'data.createDateTime');
    });

    // Sort groups by date (oldest first)
    const entries = Object.entries(groupedByDate);
    const sortedEntries = entries.sort(([, eventsA], [, eventsB]) => {
      // Get the first event's createDateTime from each group to compare dates
      const dateA = get(eventsA, '0.data.createDateTime') || '';
      const dateB = get(eventsB, '0.data.createDateTime') || '';
      return dateA.localeCompare(dateB);
    });

    return sortedEntries;
  });

  const handleUpdateYardTaskEvent = (event: YardTaskSubspaceEvent) => {
    const yardTaskId = get(event, 'yardTaskId');

    if (!yardTaskId || yardTaskId !== options.taskId?.value) {
      return;
    }

    /**
     * This is an optimistic update to the timeline events to avoid calling
     * the timeline API and having a whole update cycle.
     */
    timelineEvents.value = [
      ...timelineEvents.value,
      {
        id: event.id,
        entity: 'YardTaskEvent',
        data: event
      }
    ];
  };

  const handleUpdateCommentEvent = (event: CommentSubspaceEvent) => {
    const relatedId = get(event, 'relatedId');
    const relatedEntity = get(event, 'relatedEntity');

    if (relatedEntity !== 'YardTask' || !relatedId || relatedId !== options.taskId?.value) {
      return;
    }

    /**
     * This is an optimistic update to the timeline events to avoid calling
     * the timeline API and having a whole update cycle.
     */
    timelineEvents.value = [
      ...timelineEvents.value,
      {
        id: event.id,
        entity: 'Comment',
        data: {
          ...event,
          user: {
            id: event.userId
          }
        }
      }
    ];
  };

  onMounted(() => {
    eventHub.on('create-YardTaskEvent', handleUpdateYardTaskEvent);
    eventHub.on('create-Comment', handleUpdateCommentEvent);
  });

  onBeforeUnmount(() => {
    eventHub.off('create-YardTaskEvent', handleUpdateYardTaskEvent);
    eventHub.off('create-Comment', handleUpdateCommentEvent);
  });

  return {
    isLoading,
    timelineEvents,
    groupedEvents
  };
}
